import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FilePreviewProps, MediaFile, ExistingFile } from '../types';
import { isImage, isVideo, formatFileSize } from '../utils';

const isMediaFile = (file: MediaFile | ExistingFile): file is MediaFile => {
  return 'file' in file && 'preview' in file;
};

const isExistingFileType = (file: MediaFile | ExistingFile): file is ExistingFile => {
  return 'url' in file && 'name' in file && 'type' in file;
};

const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  type,
  onRemove,
  onDelete,
  onSetMain,
  isMain,
}) => {
  const isExistingFile = type === 'existing';

  const fileUrl =
    isExistingFile && isExistingFileType(file)
      ? file.url
      : !isExistingFile && isMediaFile(file)
        ? file.preview
        : '';

  const fileName =
    isExistingFile && isExistingFileType(file)
      ? file.name
      : !isExistingFile && isMediaFile(file)
        ? file.file.name
        : 'Unknown file';

  const fileType =
    isExistingFile && isExistingFileType(file)
      ? file.type
      : !isExistingFile && isMediaFile(file)
        ? file.file.type
        : '';

  const fileSize = !isExistingFile && isMediaFile(file) ? file.file.size : null;

  const handleRemoveClick = () => {
    if (isExistingFile && onDelete) {
      onDelete(file.id);
    } else if (!isExistingFile && onRemove) {
      onRemove(file.id);
    }
  };

  const handleSetMainClick = () => {
    if (onSetMain && !isMain) {
      onSetMain(file.id);
    }
  };

  const showImage = isImage(fileType);

  return (
    <Box
      position="relative"
      width={150}
      height={150}
      borderRadius={1}
      overflow="hidden"
      boxShadow={isMain ? 4 : 1}
      bgcolor="background.paper"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        border: isMain ? 2 : 0,
        borderColor: 'primary.main',
      }}
    >
      {isImage(fileType) ? (
        <img
          src={fileUrl}
          alt={fileName}
          width={150}
          height={150}
          style={{ objectFit: 'cover' }}
          loading="lazy"
        />
      ) : isVideo(fileType) ? (
        <video
          src={fileUrl}
          width={150}
          height={150}
          style={{ objectFit: 'cover' }}
          controls
          preload="metadata"
        />
      ) : (
        <Box p={1} textAlign="center">
          <Typography fontSize={40}>📄</Typography>
          <Typography
            variant="caption"
            textAlign="center"
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              px: 0.5,
              maxWidth: '100%',
              fontSize: '0.75rem',
            }}
          >
            {fileName}
          </Typography>
        </Box>
      )}

      {/* Main image badge */}
      {isMain && (
        <Chip
          icon={<StarIcon fontSize="small" />}
          label="Principal"
          size="small"
          color="primary"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            fontWeight: 'bold',
          }}
        />
      )}

      {/* File size for new files */}
      {!isExistingFile && fileSize && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: '#fff',
            padding: '2px 6px',
            borderRadius: 1,
            fontSize: '0.75rem',
          }}
        >
          {formatFileSize(fileSize)}
        </Box>
      )}

      {/* View button for existing files */}
      {isExistingFile && (
        <IconButton
          size="small"
          component="a"
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            backgroundColor: 'primary.main',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      )}

      {/* Set as Main button for images */}
      {isExistingFile && onSetMain && showImage && !isMain && (
        <IconButton
          size="small"
          onClick={handleSetMainClick}
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            backgroundColor: 'warning.main',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'warning.dark',
            },
          }}
          title="Establecer como principal"
        >
          <StarBorderIcon fontSize="small" />
        </IconButton>
      )}

      {/* Remove/Delete button */}
      {((isExistingFile && onDelete) || (!isExistingFile && onRemove)) && (
        <IconButton
          size="small"
          onClick={handleRemoveClick}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: 'error.main',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'error.dark',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default FilePreview;
